import {
  DoubleMaterialityESRSAreaEnum,
  type AssessmentDto,
} from "@green0meter/api-client";

type SubtopicLookup = (
  topicCode: number | string,
  subTopicCode: string,
) => { index: number } | null;

type SubSubtopicLookup = (
  topicCode: number | string,
  subTopicCode: string,
  subSubTopicCode: string,
) => { index: number } | null;

type GenerateAssessmentIdOptions = {
  subtopicLookup?: SubtopicLookup;
  subSubtopicLookup?: SubSubtopicLookup;
};

export const generateAssessmentId = (
  topic: number,
  subTopic: number | string | null,
  subSubTopic: number | string | null,
  year: number,
  existingAssessments: AssessmentDto[],
  currentAssessmentId?: string,
  options?: GenerateAssessmentIdOptions,
): string => {
  if (topic === null || topic === undefined || !year) {
    return "";
  }

  const topicCode = DoubleMaterialityESRSAreaEnum[topic];
  const parts: string[] = [topicCode];

  if (subTopic !== null && subTopic !== undefined) {
    const subTopicCode =
      typeof subTopic === "number"
        ? DoubleMaterialityESRSAreaEnum[subTopic]
        : subTopic;
    const subIndex = options?.subtopicLookup?.(topic, subTopicCode)?.index ?? null;

    if (subIndex !== null) {
      parts.push(String(subIndex + 1));
    }

    if (subSubTopic !== null && subSubTopic !== undefined) {
      const subSubTopicCode =
        typeof subSubTopic === "number"
          ? DoubleMaterialityESRSAreaEnum[subSubTopic]
          : subSubTopic;
      const subSubIndex =
        options?.subSubtopicLookup?.(topic, subTopicCode, subSubTopicCode)?.index ??
        null;

      if (subSubIndex !== null) {
        parts.push(String(subSubIndex + 1));
      }
    }
  }

  const baseId = `${parts.join("_")}_${year}`;

  return ensureUniqueAssessmentId(baseId, existingAssessments, currentAssessmentId);
};

export const ensureUniqueAssessmentId = (
  baseId: string,
  existingAssessments: AssessmentDto[],
  currentAssessmentId?: string,
): string => {
  const normalizedBase = baseId?.trim();

  if (!normalizedBase) return "";

  const existingIds = existingAssessments
    .filter((assessment) => assessment.id !== currentAssessmentId)
    .map((assessment) => assessment.assessmentId)
    .sort();

  if (!existingIds.includes(normalizedBase)) {
    return normalizedBase;
  }

  let index = 1;
  let candidate = `${normalizedBase}_${index}`;

  while (existingIds.includes(candidate)) {
    index++;
    candidate = `${normalizedBase}_${index}`;
  }

  return candidate;
};
